import { useStoryblokBridge } from '@storyblok/vue'
import { type Ref, computed, onMounted } from 'vue'
import type { StoryblokStory } from 'storyblok-generate-ts'
import type { ISbError } from 'storyblok-js-client'
import { pickBy } from 'es-toolkit'
import useAgsLog from '../useAgsLog'
import { isStaging, isDevelopment } from '~/utils/enviroment'
import { useRouter } from '#app/composables/router'
import { useCurrentShop, useRpcCall } from '#storefront/composables'
import { useRuntimeConfig } from '#app/nuxt'
import { useState } from '#app/composables/state'
import { createError } from '#app/composables/error'
import locales from '~/config/locales'
import type { NuxtError } from '#app'
import type { StoryData } from '~/cms/storyblok/types/storyblok'

const handleCmsError = (error: any): NuxtError => {
  console.log('CMS Error', error)
  console.error('CMS Error', error)
  const { status, message } =
    typeof error === 'string' ? (JSON.parse(error) as ISbError) : error
  throw createError({ statusCode: status, statusMessage: message })
}

export default <T = unknown>(key: string) => {
  // Utility Type to extract Array Element Type
  type ElementType<T> = T extends Array<infer U> ? U : T
  // Return Type based on Generic
  type DataReturnType =
    ElementType<T> extends T
    ? StoryblokStory<T>
    : StoryblokStory<ElementType<T>>[]

  const fetching = useState(`fetching-${key}`, () => false)
  const data = useState<StoryData | null>(`data-${key}`, () => null)
  const error = useState<NuxtError | undefined>(`error-${key}`, () => undefined)

  const _fetch = useRpcCall('getCmsContent')
  const resolveIdsToSlugs = useRpcCall('resolveIdsToSlugs')

  const log = useAgsLog(`useCms:${key}`)
  const config = useRuntimeConfig()
  const currentShop = useCurrentShop()
  const router = useRouter()
  const isInsideStoryblok = '_storyblok' in router.currentRoute.value.query

  const fetch = async (args: {
    slug?: string
    folder?: string
    perPage?: number
    page?: number
    contentType?: string
    resolveRelations?: string
    filterQuery?: Record<string, Record<string, any>>
  }) => {
    const lang = currentShop.value.locale
    const query = router.currentRoute.value.query || {}
    const isInsideStoryblok = '_storyblok' in query
    const storyblokRelease = query?._storyblok_release?.toString()
    let slug = args.slug || ''

    if (slug.startsWith(`${lang}/`)) {
      slug = slug.replace(`${lang}/`, '')
    } else if (slug.startsWith(`/${lang}/`)) {
      slug = slug.replace(`/${lang}/`, '')
    }

    try {
      fetching.value = true
      const options = pickBy(
        {
          starts_with: args.folder,
          per_page: args.perPage,
          language: lang,
          from_release: storyblokRelease,
          page: args.page,
          content_type: args.contentType,
          resolve_relations: args.resolveRelations,
          filter_query: args.filterQuery,
          preview: isInsideStoryblok,
        },
        (v) => v !== undefined,
      )

      data.value = await _fetch({
        slug,
        options,
      })
    } catch (e) {
      log.error(`Error fetching CMS Slug: ${slug}`, e)
      handleCmsError(e)
    } finally {
      fetching.value = false
    }
  }

  const metaLinks = computed(() => {
    const values = (data.value as any)?.translated_slugs?.map(
      (item: { lang: string; path: string; name: string }) => ({
        rel: 'alternate',
        hreflang:
          locales.find((locale) => locale.code === item.lang)?.iso || item.lang,
        href: `${config.public.baseUrl}/${item.lang}/${item.path}`,
      }),
    )

    return values || []
  })

  onMounted(() => {
    if (
      data.value &&
      data.value.id &&
      (isDevelopment || isStaging || isInsideStoryblok)
    )
      useStoryblokBridge(data.value.id, async (newStory) => {
        if (data.value?.content) {
          data.value.content = (await resolveIdsToSlugs(
            newStory.content,
          )) as StoryData['content']
        }
      })
  })

  return {
    data: data as unknown as Ref<DataReturnType>,
    fetching,
    error,
    fetchBySlug: (
      slug: string,
      params: {
        contentType?: string
        resolveRelations?: string
      } = {},
    ) => {
      return fetch({ slug, ...params })
    },
    fetchByFolder: (
      folder: string,
      params: {
        perPage?: number
        page?: number
        contentType?: string
        resolveRelations?: string
        filterQuery?: Record<string, Record<string, any>>
        resolveTags?: boolean
      } = {},
    ) => {
      return fetch({ folder, ...params })
    },
    metaLinks,
  }
}
